import { useMemo } from "react";

/**
 * useFormattedContent - A custom hook to format HTML content and check if it should be displayed
 * 
 * This hook:
 * - Replaces white backgrounds (rgb(255,255,255)) with transparent
 * - Checks if the content has meaningful text (not just HTML tags and whitespace)
 * - Returns formatted content and a boolean indicating if content should be shown
 * 
 * @param {string} content - The HTML content to format
 * @returns {Object} { formattedContent: string, hasContent: boolean }
 */
const useFormattedContent = (content) => {
  const result = useMemo(() => {
    if (!content) {
      return { formattedContent: "", hasContent: false };
    }

    // Format the content by replacing white backgrounds with transparent
    let formattedContent = content.replace(
      /background-color:\s*rgb\(255,\s*255,\s*255\)/gi,
      "background-color: transparent"
    );

    // Add allowFullScreen attribute to all iframes
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = formattedContent;
    const iframes = tempDiv.querySelectorAll("iframe");
    
    if (iframes.length > 0) {
      iframes.forEach((iframe) => {
        if (!iframe.hasAttribute('allowFullScreen')) {
          iframe.setAttribute('allowFullScreen', '');
          iframe.setAttribute('allowfullscreen', '');
          iframe.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share');
        }
      });
      formattedContent = tempDiv.innerHTML;
    }

    // Check if content has meaningful text (not just HTML tags and whitespace)
    // Strip HTML tags and check if there's any non-whitespace content
    const textContent = content
      .replace(/<[^>]*>/g, "") // Remove HTML tags
      .replace(/&nbsp;/g, " ") // Replace &nbsp; with space
      .replace(/\s+/g, " ") // Normalize whitespace
      .trim();

    const hasContent = textContent.length > 0;

    return { formattedContent, hasContent };
  }, [content]);

  return result;
};

export default useFormattedContent;
